import { JobAdList, JobPostingForm } from './job-ad-duplication';

# Job ad duplication

As we enable hirers to advertise positions via more than one [solution] at once,
there will be scenarios where job ads with the same content will be posted to SEEK.
Duplication fragments analytics and candidates across multiple job ads,
making it harder to track performance and manage candidates effectively,
and provides poor return on investment,
as our search and recommendation models will detect and suppress duplicate job ads.

The SEEK marketplace has layered controls in place to manage duplicate job ads,
with stricter deduplication rules applied to automated posting solutions such as [Ad Sync].

[Ad Sync]: /use-cases/job-posting/ad-sync
[solution]: /use-cases/job-posting#solutions

## Managing hirer duplication

Provide wayfinding for hirers to view existing job ads for a given position before posting a new one.
This is especially important if they have access to an automated posting solution like Ad Sync.

Your software may already support this if its job posting flow begins on a job requisition view:

<JobAdList />

If hirers can navigate to your job posting form without viewing the job ad list,
provide a notice that indicates whether the given position has existing job ads and a link for the hirer to review them.
The hirer must still be allowed to proceed to post another job ad if they so choose.

<JobPostingForm />

## Managing system duplication

Requests that carry a Job Posting [mutation] must be [idempotent].
This removes the risk of unintentionally posting duplicate job ads when your software needs to [retry] a request due to an error.

A unique `idempotencyId` is required when posting a job ad.
We recommend that you use a generate a new [v4 UUID] before sending the first request, and to reuse that identifier on all subsequent retries for the same job ad.
This provides a common thread across all attempts,
making it easier to manage incidents and avoid manual triage of requests that initially experienced transient errors but eventually succeeded.

If your software has already posted a job ad with the same `idempotencyId`,
the SEEK API will return a [`PostPositionPayload_Conflict`] or [`PostPositionProfileForOpeningPayload_Conflict`] result in accordance with our [mutation conflict] convention.
If your software was retrying a job posting request,
you can treat the conflict result as a success as it indicates the job ad has already been posted.

The SEEK API deduplicates job ads posted by your software based on their `idempotencyId`s.
Your software must never reuse an `idempotencyId` when posting a different job ad;
this may result in subsequent job ads being missed or other unexpected behaviour.
Your identifiers are isolated and will not conflict with those generated by other recruitment software providers.

Ad Sync provides similar handling to ensure a single job ad exists based on a stable identifier that your software provides for a given position, `seekPartnerPositionId`.
Refer to [Sync positions] for further details.

[Sync positions]: /use-cases/job-posting/ad-sync/sync-positions#seekpartnerpositionid

## Consolidating job applications

The hirer experience on the SEEK Employer website is currently job ad centric rather than position centric.
Hirers that use [SEEK’s Apply Form] will rely on your software to implement [Optimised Apply] and provide a consolidated applicant list view across multiple job ads.

Include consistent [references] across posting sources to allow your software to associate applicants across multiple job ads with the same position.

[SEEK’s Apply Form]: /use-cases/job-posting/application-methods#seeks-apply-form
[Optimised Apply]: /use-cases/application-export
[references]: /use-cases/job-posting/identifiers-and-references
[`PostPositionPayload_Conflict`]: /schema/named-type/PostPositionPayload_Conflict
[`PostPositionProfileForOpeningPayload_Conflict`]: /schema/named-type/PostPositionProfileForOpeningPayload_Conflict
[idempotent]: https://en.wikipedia.org/wiki/Idempotence#Computer_science_meaning
[mutation conflict]: /graphql/seek-api-conventions#mutation-conflicts
[mutation]: /graphql/in-practice#operations
[retry]: /graphql/error-responses#retrying-server-errors
[v4 UUID]: https://en.wikipedia.org/wiki/Universally_unique_identifier#Version_4_(random)
